/* Public shipping facts only. This selector never changes the shopper's market or cart. */
if (!customElements.get('ruemood-shipping-details')) {
  customElements.define('ruemood-shipping-details', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const source = this.querySelector('[data-shipping-config]');
      this.destination = this.querySelector('[data-shipping-country]');
      if (!source || !this.destination) return;
      try { this.config = JSON.parse(source.textContent); } catch (_) { return; }
      if (!this.config || this.config.schemaVersion !== 1) return;
      this.initialized = true;
      this.destination.addEventListener('change', () => {
        this.updateEstimate();
        this.syncPicker();
      });
      this.updateEstimate();
      this.destination.disabled = false;
      this.initPicker();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.removeViewportListeners();
    }

    initPicker() {
      this.dialog = this.querySelector('[data-country-dialog]');
      if (typeof this.dialog?.showModal !== 'function') return;
      this.trigger = this.querySelector('[data-country-trigger]');
      this.search = this.querySelector('[data-country-search]');
      this.list = this.querySelector('[data-country-list]');
      this.clear = this.querySelector('[data-country-clear]');
      this.rows = Array.from(this.destination.options, option => {
        const row = document.createElement('li');
        row.id = `${this.list.id}-${option.value}`;
        row.setAttribute('role', 'option');
        row.dataset.countryOption = option.value;
        row.innerHTML = '<span></span><svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="m4 10 4 4 8-8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        row.querySelector('span').textContent = option.textContent;
        const aliases = {GB: 'uk britain', US: 'usa america', AE: 'uae'};
        row.searchText = this.normalize(`${option.textContent} ${option.value} ${aliases[option.value] || ''}`);
        this.list.append(row);
        return row;
      });
      this.trigger.addEventListener('click', () => this.openPicker());
      this.trigger.addEventListener('keyup', event => {
        // Keep Escape from reaching the theme's parent-accordion shortcut.
        if (event.key === 'Escape') event.stopPropagation();
      });
      this.querySelector('[data-country-close]').addEventListener('click', () => this.closePicker());
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.closePicker();
      });
      this.dialog.addEventListener('close', () => {
        this.trigger.setAttribute('aria-expanded', 'false');
        this.removeViewportListeners();
        if (this.isConnected) this.trigger.focus({preventScroll: true});
      });
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closePicker();
        }
        const row = event.target.closest('[data-country-option]');
        if (row && this.list.contains(row) && !row.hidden) this.chooseCountry(row.dataset.countryOption);
      });
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.closePicker();
        } else if (event.key === 'Tab') {
          const controls = [this.querySelector('[data-country-close]'), this.search, this.clear].filter(control => !control.hidden);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });
      this.dialog.addEventListener('keyup', event => {
        if (event.key === 'Escape') event.stopPropagation();
      });
      this.search.addEventListener('input', () => this.filterCountries());
      this.search.addEventListener('keydown', event => {
        if (event.isComposing) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const index = this.filtered.indexOf(this.activeRow);
          const next = Math.max(0, Math.min(this.filtered.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
          this.setActive(this.filtered[next]);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          if (this.activeRow) this.chooseCountry(this.activeRow.dataset.countryOption);
        }
      });
      this.clear.addEventListener('click', () => {
        this.search.value = '';
        this.filterCountries();
        this.search.focus({preventScroll: true});
      });
      this.onViewportChange = () => this.positionPicker();
      this.syncPicker();
      this.destination.hidden = true;
      this.querySelector('[data-country-native-caret]').setAttribute('hidden', '');
      this.trigger.hidden = false;
      this.querySelector(`label[for="${this.destination.id}"]`).htmlFor = this.trigger.id;
    }

    normalize(value) {
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    }

    syncPicker() {
      if (!this.rows) return;
      this.querySelector('[data-country-selected]').textContent = this.destination.selectedOptions[0]?.textContent || '';
      this.rows.forEach(row => row.setAttribute('aria-selected', String(row.dataset.countryOption === this.destination.value)));
    }

    openPicker() {
      if (this.dialog.open) return;
      this.search.value = '';
      this.filterCountries();
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.positionPicker();
      this.search.focus({preventScroll: true});
      this.setActive(this.rows.find(row => row.dataset.countryOption === this.destination.value), true);
      window.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('scroll', this.onViewportChange);
    }

    closePicker() {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.dialog.close();
    }

    removeViewportListeners() {
      if (!this.onViewportChange) return;
      window.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
    }

    filterCountries() {
      const query = this.normalize(this.search.value);
      const words = query.split(' ').filter(Boolean);
      const alias = {uk: 'GB', usa: 'US', uae: 'AE'}[query];
      const exact = this.rows.find(row => row.dataset.countryOption.toLowerCase() === query || row.dataset.countryOption === alias);
      this.rows.forEach(row => { row.hidden = exact ? row !== exact : !words.every(word => row.searchText.includes(word)); });
      this.filtered = this.rows.filter(row => !row.hidden);
      this.list.hidden = this.filtered.length === 0;
      this.clear.hidden = !this.search.value;
      this.querySelector('[data-country-empty]').hidden = this.filtered.length > 0;
      this.querySelector('[data-country-count]').textContent = this.filtered.length;
      this.querySelector('[data-country-list-heading]').textContent = words.length ? 'Search results' : 'All countries';
      this.querySelector('[data-country-status]').textContent = `${this.filtered.length} ${this.filtered.length === 1 ? 'country' : 'countries'} found`;
      this.setActive(this.filtered[0]);
      this.list.scrollTop = 0;
      this.positionPicker();
    }

    setActive(row, center = false) {
      this.activeRow?.removeAttribute('data-active');
      this.activeRow = row;
      if (!row) {
        this.search.removeAttribute('aria-activedescendant');
        return;
      }
      row.setAttribute('data-active', '');
      this.search.setAttribute('aria-activedescendant', row.id);
      const top = row.offsetTop;
      if (center) this.list.scrollTop = top - (this.list.clientHeight - row.offsetHeight) / 2;
      else if (top < this.list.scrollTop) this.list.scrollTop = top;
      else if (top + row.offsetHeight > this.list.scrollTop + this.list.clientHeight) this.list.scrollTop = top + row.offsetHeight - this.list.clientHeight;
    }

    chooseCountry(code) {
      if (!this.rows.some(row => row.dataset.countryOption === code)) return;
      this.destination.value = code;
      this.destination.dispatchEvent(new Event('change', {bubbles: true}));
      this.closePicker();
    }

    positionPicker() {
      if (!this.dialog.open) return;
      const viewport = window.visualViewport;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      const x = viewport?.offsetLeft || 0;
      const y = viewport?.offsetTop || 0;
      const rect = this.trigger.getBoundingClientRect();
      let panelWidth, panelHeight, left, top, anchor;
      if (width < 750) {
        panelWidth = width;
        panelHeight = Math.min(560, height - 12);
        left = x;
        anchor = 'bottom';
      } else {
        panelWidth = Math.min(480, Math.max(320, rect.width), width - 32);
        panelHeight = Math.min(440, height - 32);
        left = Math.max(x + 16, Math.min(rect.left, x + width - panelWidth - 16));
        const below = y + height - rect.bottom - 24;
        const above = rect.top - y - 24;
        if (Math.max(below, above) < 280) anchor = 'center';
        else if (below >= Math.min(320, panelHeight) || below >= above) {
          panelHeight = Math.min(panelHeight, below);
          top = rect.bottom + 8;
        } else {
          panelHeight = Math.min(panelHeight, above);
          anchor = 'above';
        }
      }
      this.dialog.style.setProperty('--country-picker-x', `${left}px`);
      this.dialog.style.setProperty('--country-picker-width', `${panelWidth}px`);
      this.dialog.style.setProperty('--country-picker-height', `${panelHeight}px`);
      // Measure after width/max-height updates so short lists and wrapped names fit naturally.
      const contentHeight = this.dialog.getBoundingClientRect().height;
      if (anchor === 'bottom') top = y + height - contentHeight;
      else if (anchor === 'center') top = y + (height - contentHeight) / 2;
      else if (anchor === 'above') top = rect.top - 8 - contentHeight;
      if (width >= 750) top = Math.max(y + 16, Math.min(top, y + height - contentHeight - 16));
      this.dialog.style.setProperty('--country-picker-y', `${top}px`);
    }

    updateEstimate() {
      const code = this.destination.value;
      const transit = this.config.transit?.[code];
      const fees = this.config.fees?.[code] || this.config.fees?.other;
      this.querySelector('[data-transit-section]').hidden = !transit;
      this.querySelector('[data-transit]').textContent = transit
        ? `${transit.min}–${transit.max} business days`
        : '';
      const note = this.querySelector('[data-transit-note]');
      note.textContent = transit?.note || '';
      note.hidden = !note.textContent;
      this.querySelector('[data-peak-fee]').textContent = this.formatFee(fees?.peak);
      this.querySelector('[data-offpeak-fee]').textContent = this.formatFee(fees?.offpeak);
    }

    formatFee(amount) {
      return amount === '0.00' ? 'Free' : amount ? `US$${amount}` : 'At checkout';
    }
  });
}
